import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  Modal,
  TextInput,
  Alert,
  Image,
  Switch,
  RefreshControl,
} from 'react-native';
import { useAuth } from '@/contexts/AuthContext';
import { useGrupos } from '@/contexts/GruposContext';
import { useRacha } from '@/contexts/RachaContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Plus, Users, Edit, Trash2, UserPlus, UserMinus, Check, ChevronDown, ChevronUp } from 'lucide-react-native';
import ChavePixSelector from '@/components/ChavePixSelector';
import { ChavePix } from '@/types/app';
import LoadingSpinner from '@/components/LoadingSpinner';
import SkeletonCard from '@/components/SkeletonCard';
import SearchFilter from '@/components/SearchFilter';
import MigrationModal from '@/components/MigrationModal';
import BannerMenor from '@/components/BannerMenor';

interface NovoParticipante {
  nome: string;
  email: string;
  chavePix?: ChavePix;
}

export default function GruposScreen() {
  const { user } = useAuth();
  const { grupos, createGrupo, updateGrupo, deleteGrupo, addParticipanteToGrupo, removeParticipanteFromGrupo, getAllParticipantes } = useGrupos();
  const { showMigrationModal, setShowMigrationModal, startMigration } = useRacha();
  const { colors } = useTheme();
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddParticipanteModal, setShowAddParticipanteModal] = useState(false);
  const [editingGrupo, setEditingGrupo] = useState<any>(null);
  const [selectedGrupoId, setSelectedGrupoId] = useState<string>('');
  
  const [novoGrupoNome, setNovoGrupoNome] = useState('');
  const [novoGrupoEmoji, setNovoGrupoEmoji] = useState('👥');
  const [incluirCriador, setIncluirCriador] = useState(false);
  const [novoParticipante, setNovoParticipante] = useState<NovoParticipante>({ nome: '', email: '' });
  const [participantesSelecionados, setParticipantesSelecionados] = useState<string[]>([]);

  // Estados para loading e feedback
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Estados para busca e filtros
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  
  // Estado para controlar cards expandidos
  const [cardsExpandidos, setCardsExpandidos] = useState<Set<string>>(new Set());

  const todosParticipantes = getAllParticipantes();

  // Estados para alertas nativos
  const [showNativeAlert, setShowNativeAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{
    title: string;
    message: string;
    onConfirm?: () => void;
    showCancel?: boolean;
  }>({ title: '', message: '' });

  // Simular carregamento inicial
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  // Cards iniciam recolhidos por padrão
  // useEffect removido para permitir que os grupos iniciem no estado recolhido

  // Função de refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Simular refresh dos dados
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsRefreshing(false);
  };

  // Funções de busca e filtro
  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleFilter = (filter: string) => {
    setActiveFilter(filter);
  };

  // Filtrar grupos
  const getGruposFiltrados = () => {
    let gruposFiltrados = grupos;
    
    // Aplicar busca
    if (searchQuery.trim()) {
      gruposFiltrados = gruposFiltrados.filter(grupo => 
        grupo.nome.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Aplicar filtro
    if (activeFilter) {
      if (activeFilter === 'participando') {
        gruposFiltrados = gruposFiltrados.filter(grupo => 
          grupo.participantes.some(p => p.id === user?.id)
        );
      }
    }
    
    return gruposFiltrados;
  };

  // Função para alternar expansão do card
  const toggleCardExpansion = (grupoId: string) => {
    setCardsExpandidos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(grupoId)) {
        newSet.delete(grupoId);
      } else {
        newSet.add(grupoId);
      }
      return newSet;
    });
  };

  const gruposParaExibir = getGruposFiltrados();

  const criarGrupo = async () => {
    if (!novoGrupoNome.trim()) {
      showAlert('Erro', 'Digite um nome para o grupo', undefined, false);
      return;
    }

    setIsLoading(true);
    try {
      const participantesParaAdicionar = todosParticipantes.filter(p => 
        participantesSelecionados.includes(p.id)
      );

      await createGrupo(
        novoGrupoNome.trim(),
        novoGrupoEmoji,
        participantesParaAdicionar,
        incluirCriador
      );
      
      // Limpar formulário
      setNovoGrupoNome('');
      setNovoGrupoEmoji('👥');
      setParticipantesSelecionados([]);
      setIncluirCriador(false);
      setShowCreateModal(false);

      showAlert('Sucesso', 'Grupo criado com sucesso!', undefined, false);
    } catch {
      showAlert('Erro', 'Falha ao criar grupo', undefined, false);
    } finally {
      setIsLoading(false);
    }
  };

  const editarGrupo = async () => {
    if (!novoGrupoNome.trim()) {
      showAlert('Erro', 'Digite um nome para o grupo', undefined, false);
      return;
    }

    try {
      const participantesParaAdicionar = todosParticipantes.filter(p => 
        participantesSelecionados.includes(p.id)
      );

      // Combinar participantes existentes com novos
      const participantesFinais = [...(editingGrupo?.participantes || []), ...participantesParaAdicionar];

      await updateGrupo(editingGrupo!.id, {
        nome: novoGrupoNome.trim(),
        emoji: novoGrupoEmoji,
        participantes: participantesFinais
      });

      // Limpar formulário
      setNovoGrupoNome('');
      setNovoGrupoEmoji('👥');
      setParticipantesSelecionados([]);
      setIncluirCriador(false);
      setEditingGrupo(null);
      setShowEditModal(false);

      showAlert('Sucesso', 'Grupo atualizado com sucesso!', undefined, false);
    } catch {
      showAlert('Erro', 'Falha ao editar grupo', undefined, false);
    }
  };

  const confirmarExclusaoGrupo = (grupo: any) => {
    showAlert(
      'Excluir Grupo',
      `Tem certeza que deseja excluir o grupo "${grupo.nome}"? Esta ação não pode ser desfeita.`,
      async () => {
        try {
          await deleteGrupo(grupo.id);
          showAlert('Sucesso', 'Grupo excluído com sucesso!', undefined, false);
        } catch {
          showAlert('Erro', 'Falha ao excluir grupo', undefined, false);
        }
      }
    );
  };

  const abrirEdicao = (grupo: any) => {
    setEditingGrupo(grupo);
    setNovoGrupoNome(grupo.nome);
    setNovoGrupoEmoji(grupo.emoji);
    setShowEditModal(true);
  };

  // Função de alert que funciona no web e mobile
  const showAlert = (title: string, message: string, onConfirm?: () => void, showCancel: boolean = true) => {
    setAlertConfig({ title, message, onConfirm, showCancel });
    setShowNativeAlert(true);
  };

  const adicionarParticipante = async () => {
    // Verificar se está tentando adicionar novo participante E selecionar existente ao mesmo tempo
    const temNovoParticipante = novoParticipante.nome.trim() !== '';
    const temParticipantesExistentes = participantesSelecionados.length > 0;
    const incluirCriadorSelecionado = incluirCriador;

    // Não permitir adicionar novo participante e selecionar existente ao mesmo tempo
    if (temNovoParticipante && temParticipantesExistentes) {
      showAlert('Erro', 'Você não pode adicionar um novo participante e selecionar participantes existentes ao mesmo tempo. Escolha uma das opções.', undefined, false);
      return;
    }

    // Verificar se há pelo menos uma ação
    if (!temNovoParticipante && !temParticipantesExistentes && !incluirCriadorSelecionado) {
      showAlert('Erro', 'Selecione pelo menos uma opção: adicionar novo participante, selecionar participante existente ou incluir você mesmo no grupo.', undefined, false);
      return;
    }

    try {
      // Se há um novo participante para adicionar
      if (temNovoParticipante) {
        const participante = {
          id: Date.now().toString(),
          nome: novoParticipante.nome.trim(),
          email: novoParticipante.email.trim() || undefined,
          foto: 'https://images.pexels.com/photos/771742/pexels-photo-771742.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&dpr=2',
          chavePix: novoParticipante.chavePix
        };

        await addParticipanteToGrupo(selectedGrupoId, participante);
        setNovoParticipante({ nome: '', email: '' });
        showAlert('Sucesso', 'Participante adicionado com sucesso!', undefined, false);
      }

      // Se há participantes existentes selecionados
      if (temParticipantesExistentes) {
        const participantesParaAdicionar = todosParticipantes.filter(p => 
          participantesSelecionados.includes(p.id)
        );
        
        for (const participante of participantesParaAdicionar) {
          await addParticipanteToGrupo(selectedGrupoId, participante);
        }
        
        setParticipantesSelecionados([]);
        showAlert('Sucesso', `${participantesParaAdicionar.length} participante(s) adicionado(s) com sucesso!`, undefined, false);
      }

      // Se deve incluir o criador
      if (incluirCriadorSelecionado) {
        const participanteCriador = {
          id: user?.id || Date.now().toString(),
          nome: user?.nome || 'Usuário',
          email: user?.email,
          foto: user?.fotoURL || 'https://images.pexels.com/photos/771742/pexels-photo-771742.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&dpr=2',
          chavePix: user?.chavePix
        };

        await addParticipanteToGrupo(selectedGrupoId, participanteCriador);
        setIncluirCriador(false);
        showAlert('Sucesso', 'Você foi adicionado ao grupo com sucesso!', undefined, false);
      }

      setShowAddParticipanteModal(false);
    } catch {
      showAlert('Erro', 'Falha ao adicionar participante', undefined, false);
    }
  };

  const confirmarRemocaoParticipante = (grupoId: string, participanteId: string, participanteNome: string) => {
    showAlert(
      'Remover Participante',
      `Tem certeza que deseja remover "${participanteNome}" do grupo?`,
      async () => {
        try {
          await removeParticipanteFromGrupo(grupoId, participanteId);
          showAlert('Sucesso', `${participanteNome} foi removido do grupo!`, undefined, false);
        } catch {
          showAlert('Erro', 'Falha ao remover participante', undefined, false);
        }
      }
    );
  };

  const toggleParticipante = (participanteId: string) => {
    // Verificar se o participante já está no grupo atual (para adição de participantes a grupos existentes)
    let grupoAtual = null;
    
    if (selectedGrupoId && showAddParticipanteModal) {
      // Modal de adicionar participante
      grupoAtual = grupos.find(g => g.id === selectedGrupoId);
    } else if (editingGrupo && showEditModal) {
      // Modal de editar grupo
      grupoAtual = editingGrupo;
    }
    
    if (grupoAtual) {
      const participanteJaEstaNoGrupo = grupoAtual.participantes.some((p: any) => p.id === participanteId);
      
      if (participanteJaEstaNoGrupo) {
        const participante = todosParticipantes.find(p => p.id === participanteId);
        const nomeParticipante = participante?.nome || 'Participante';
        // Usar window.alert para web que sempre aparece por cima
        if (typeof window !== 'undefined') {
          window.alert(`${nomeParticipante} já está neste grupo!`);
        } else {
          Alert.alert('Aviso', `${nomeParticipante} já está neste grupo!`);
        }
        return;
      }
    }
    
    setParticipantesSelecionados(prev => {
      if (prev.includes(participanteId)) {
        return prev.filter(id => id !== participanteId);
      } else {
        return [...prev, participanteId];
      }
    });
  };

  const toggleIncluirCriador = (value: boolean) => {
    if (value) {
      // Durante a criação de um novo grupo, não há grupoId para verificar
      // Durante a edição, verificar se o usuário já está no grupo
      if (editingGrupo) {
        const grupo = grupos.find(g => g.id === editingGrupo.id);
        
        if (grupo) {
          const usuarioJaEstaNoGrupo = grupo.participantes.some(p => p.id === user?.id);
          
          if (usuarioJaEstaNoGrupo) {
            showAlert('Aviso', 'Você já está neste grupo!', undefined, false);
            return; // Não ativa o switch
          }
        }
      }
      // Durante a criação, não há verificação necessária pois o grupo ainda não existe
    }
    
    setIncluirCriador(value);
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: 24,
      paddingVertical: 16,
      backgroundColor: colors.surface,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    headerTitle: {
      fontSize: 22,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
    },
    content: {
      flex: 1,
      paddingHorizontal: 24,
    },
    emptyState: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 80,
    },
    emptyTitle: {
      fontSize: 18,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 8,
    },
    emptySubtitle: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      textAlign: 'center',
      lineHeight: 20,
    },
    gruposList: {
      paddingVertical: 16,
      gap: 12,
    },
    grupoCard: {
      backgroundColor: colors.surface,
      borderRadius: 16,
      padding: 20,
      borderWidth: 1,
      borderColor: colors.border,
      elevation: 2,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.1,
      shadowRadius: 3,
    },
    grupoHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: 16,
    },
    grupoInfo: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
    },
    grupoEmoji: {
      fontSize: 32,
      marginRight: 16,
    },
    grupoDetails: {
      flex: 1,
    },
    grupoNome: {
      fontSize: 18,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 4,
    },
    participantesCount: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    participantesTexto: {
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
    },
    grupoActions: {
      flexDirection: 'row',
      gap: 8,
    },
    actionButton: {
      padding: 8,
      borderRadius: 8,
      backgroundColor: colors.background,
    },
    participantesList: {
      gap: 8,
    },
    participanteItem: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.background,
      borderRadius: 12,
      padding: 12,
      gap: 12,
    },
    participanteFoto: {
      width: 32,
      height: 32,
      borderRadius: 16,
    },
    participanteInfo: {
      flex: 1,
    },
    participanteNome: {
      fontSize: 14,
      fontFamily: 'Inter-Medium',
      color: colors.text,
      marginBottom: 2,
    },
    participanteEmail: {
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
    },
    participantePix: {
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      color: colors.primary,
    },
    removeButton: {
      padding: 4,
    },
    addParticipanteButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.primary + '20',
      borderRadius: 12,
      padding: 12,
      gap: 8,
      borderWidth: 1,
      borderColor: colors.primary + '40',
      borderStyle: 'dashed',
    },
    addParticipanteButtonText: {
      fontSize: 14,
      fontFamily: 'Inter-SemiBold',
      color: colors.primary,
    },
    fab: {
      position: 'absolute',
      bottom: 24,
      right: 24,
      width: 56,
      height: 56,
      borderRadius: 28,
      backgroundColor: colors.primary,
      justifyContent: 'center',
      alignItems: 'center',
      elevation: 4,
      shadowColor: colors.primary,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 8,
    },
    modalContainer: {
      flex: 1,
      backgroundColor: colors.background,
    },
    modalHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: 24,
      paddingVertical: 16,
      backgroundColor: colors.surface,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    modalCancelButton: {
      fontSize: 16,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
    },
    modalTitle: {
      fontSize: 18,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
    },
    modalCreateButton: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      color: colors.primary,
    },
    modalContent: {
      flex: 1,
      paddingHorizontal: 24,
      paddingTop: 24,
    },
    emojiSelector: {
      marginBottom: 24,
    },
    selectedEmoji: {
      fontSize: 64,
      textAlign: 'center',
      marginBottom: 16,
    },
    emojiList: {
      flexDirection: 'row',
      gap: 8,
      paddingHorizontal: 8,
    },
    emojiOption: {
      width: 44,
      height: 44,
      borderRadius: 22,
      backgroundColor: colors.surface,
      justifyContent: 'center',
      alignItems: 'center',
      borderWidth: 2,
      borderColor: 'transparent',
    },
    emojiSelected: {
      borderColor: colors.primary,
      backgroundColor: colors.primary + '20',
    },
    emojiOptionText: {
      fontSize: 20,
    },
    inputContainer: {
      marginBottom: 20,
    },
    labelRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: 8,
      marginBottom: 4,
    },
    inputLabel: {
      fontSize: 14,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 4,
    },
    input: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      paddingHorizontal: 16,
      height: 56,
      fontSize: 16,
      fontFamily: 'Inter-Regular',
      color: colors.text,
      borderWidth: 1,
      borderColor: colors.border,
    },
    optionalLabel: {
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
    },
    participantesSection: {
      marginTop: 24,
    },
    sectionTitle: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 16,
    },
    participantesDisponiveis: {
      gap: 8,
    },
    participanteOption: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 16,
      borderWidth: 2,
      borderColor: colors.border,
      gap: 12,
    },
    participanteSelected: {
      borderColor: colors.primary,
      backgroundColor: colors.primary + '20',
    },
    participanteFotoSmall: {
      width: 32,
      height: 32,
      borderRadius: 16,
    },
    participanteOptionNome: {
      fontSize: 14,
      fontFamily: 'Inter-Medium',
      color: colors.text,
      flex: 1,
    },
    checkbox: {
      width: 20,
      height: 20,
      borderRadius: 10,
      borderWidth: 2,
      borderColor: colors.border,
      justifyContent: 'center',
      alignItems: 'center',
    },
    checkboxSelected: {
      borderColor: colors.primary,
      backgroundColor: colors.primary,
    },
    incluirCriadorSection: {
      marginTop: 24,
    },
    incluirCriadorRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    incluirCriadorLabel: {
      fontSize: 14,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
    },
    incluirCriadorDescription: {
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
    },
    divider: {
      height: 1,
      backgroundColor: colors.border,
      marginVertical: 16,
    },
    dividerText: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      textAlign: 'center',
    },
    alertOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 99999,
      elevation: 99999,
    },
    alertContainer: {
      backgroundColor: colors.surface,
      padding: 24,
      borderRadius: 16,
      width: '80%',
      maxWidth: 400,
    },
    alertTitle: {
      fontSize: 18,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 16,
    },
    alertMessage: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      marginBottom: 24,
    },
    alertButtons: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    alertButton: {
      padding: 12,
      borderRadius: 8,
      backgroundColor: colors.primary + '20',
    },
    alertButtonCancel: {
      backgroundColor: colors.error + '20',
    },
    alertButtonCancelText: {
      fontSize: 14,
      fontFamily: 'Inter-SemiBold',
      color: colors.error,
    },
    alertButtonConfirm: {
      backgroundColor: colors.primary,
    },
    alertButtonConfirmText: {
      fontSize: 14,
      fontFamily: 'Inter-SemiBold',
      color: '#FFFFFF',
    },
  });

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Meus Grupos</Text>
      </View>

      {/* Banner menor para usuários gratuitos */}
      <BannerMenor />

      <SearchFilter
        onSearch={handleSearch}
        onFilter={handleFilter}
        searchPlaceholder="Buscar grupos..."
        filterOptions={[
          { label: 'Participando', value: 'participando' },
        ]}
        activeFilter={activeFilter}
      />

      <ScrollView 
        style={styles.content} 
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            colors={[colors.primary]}
            tintColor={colors.primary}
          />
        }
      >
        {isInitialLoading ? (
          // Skeleton loading
          <View style={styles.gruposList}>
            {[1, 2, 3].map((i) => (
              <View key={i} style={styles.grupoCard}>
                <SkeletonCard height={60} style={{ marginBottom: 12 }} />
                <SkeletonCard height={40} width="80%" />
                <SkeletonCard height={40} width="60%" />
              </View>
            ))}
          </View>
        ) : gruposParaExibir.length === 0 ? (
          <View style={styles.emptyState}>
            <Text style={styles.emptyTitle}>Nenhum grupo ainda</Text>
            <Text style={styles.emptySubtitle}>
              Crie grupos de participantes para facilitar a criação de rachas
            </Text>
          </View>
        ) : (
          <View style={styles.gruposList}>
            {gruposParaExibir.map((grupo) => (
              <View key={grupo.id} style={styles.grupoCard}>
                <View style={styles.grupoHeader}>
                  <View style={styles.grupoInfo}>
                    <Text style={styles.grupoEmoji}>{grupo.emoji}</Text>
                    <View style={styles.grupoDetails}>
                      <Text style={styles.grupoNome}>{grupo.nome}</Text>
                      <View style={styles.participantesCount}>
                        <Users size={14} color={colors.textSecondary} />
                        <Text style={styles.participantesTexto}>
                          {grupo.participantes.length} participantes
                        </Text>
                      </View>
                    </View>
                  </View>
                  
                  <View style={styles.grupoActions}>
                    <TouchableOpacity
                      style={styles.actionButton}
                      onPress={() => toggleCardExpansion(grupo.id)}
                      activeOpacity={0.7}
                    >
                      {cardsExpandidos.has(grupo.id) ? (
                        <ChevronUp size={16} color={colors.textSecondary} />
                      ) : (
                        <ChevronDown size={16} color={colors.textSecondary} />
                      )}
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.actionButton}
                      onPress={() => abrirEdicao(grupo)}
                      activeOpacity={0.7}
                    >
                      <Edit size={16} color={colors.primary} />
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.actionButton}
                      onPress={() => confirmarExclusaoGrupo(grupo)}
                      activeOpacity={0.7}
                    >
                      <Trash2 size={16} color={colors.error} />
                    </TouchableOpacity>
                  </View>
                </View>
                
                {cardsExpandidos.has(grupo.id) && (
                  <View style={styles.participantesList}>
                  {grupo.participantes.map((participante: any) => (
                    <View key={participante.id} style={styles.participanteItem}>
                      <Image source={{ uri: participante.foto }} style={styles.participanteFoto} />
                      <View style={styles.participanteInfo}>
                        <Text style={styles.participanteNome}>{participante.nome}</Text>
                        {participante.email && (
                          <Text style={styles.participanteEmail}>{participante.email}</Text>
                        )}
                        {participante.chavePix && (
                          <Text style={styles.participantePix}>
                            PIX: {participante.chavePix.valor} ({participante.chavePix.tipo})
                          </Text>
                        )}
                      </View>
                      <TouchableOpacity
                        style={styles.removeButton}
                        onPress={() => confirmarRemocaoParticipante(grupo.id, participante.id, participante.nome)}
                        activeOpacity={0.7}
                      >
                        <UserMinus size={16} color={colors.error} />
                      </TouchableOpacity>
                    </View>
                  ))}
                  
                  <TouchableOpacity 
                    style={styles.addParticipanteButton}
                    onPress={() => {
                      setSelectedGrupoId(grupo.id);
                      setShowAddParticipanteModal(true);
                    }}
                    activeOpacity={0.7}
                  >
                    <UserPlus size={16} color={colors.primary} />
                    <Text style={styles.addParticipanteButtonText}>Adicionar participante</Text>
                  </TouchableOpacity>
                  </View>
                )}
              </View>
            ))}
          </View>
        )}
      </ScrollView>

      <TouchableOpacity
        style={styles.fab}
        onPress={() => setShowCreateModal(true)}
        activeOpacity={0.8}
      >
        <Plus size={24} color="#FFFFFF" />
      </TouchableOpacity>

      {/* Modal Criar Grupo */}
      <Modal
        visible={showCreateModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowCreateModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity onPress={() => setShowCreateModal(false)}>
              <Text style={styles.modalCancelButton}>Cancelar</Text>
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Novo Grupo</Text>
            <TouchableOpacity onPress={criarGrupo}>
              <Text style={styles.modalCreateButton}>Criar</Text>
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.modalContent}>
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <>
                <View style={styles.emojiSelector}>
              <Text style={styles.selectedEmoji}>{novoGrupoEmoji}</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.emojiList}>
                  {['👥', '👨‍👩‍👧‍👦', '🏠', '🎉', '🍻', '✈️', '🏖️', '🎬', '🏋️', '🎮'].map((emoji) => (
                    <TouchableOpacity
                      key={emoji}
                      style={[
                        styles.emojiOption,
                        novoGrupoEmoji === emoji && styles.emojiSelected
                      ]}
                      onPress={() => setNovoGrupoEmoji(emoji)}
                    >
                      <Text style={styles.emojiOptionText}>{emoji}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.inputLabel}>Nome do grupo</Text>
              <TextInput
                style={styles.input}
                placeholder="Ex: Família, Amigos da faculdade"
                placeholderTextColor={colors.textSecondary}
                value={novoGrupoNome}
                onChangeText={setNovoGrupoNome}
                maxLength={30}
              />
            </View>

            {todosParticipantes.length > 0 && (
              <View style={styles.participantesSection}>
                <Text style={styles.sectionTitle}>Adicionar participantes existentes (após a criação do grupo você poderá adicionar outros participantes)</Text>
                <View style={styles.participantesDisponiveis}>
                  {todosParticipantes.map((participante) => (
                    <TouchableOpacity
                      key={participante.id}
                      style={[
                        styles.participanteOption,
                        participantesSelecionados.includes(participante.id) && styles.participanteSelected
                      ]}
                      onPress={() => toggleParticipante(participante.id)}
                    >
                      <Image source={{ uri: participante.foto }} style={styles.participanteFotoSmall} />
                      <Text style={styles.participanteOptionNome}>{participante.nome}</Text>
                      <View style={[
                        styles.checkbox,
                        participantesSelecionados.includes(participante.id) && styles.checkboxSelected
                      ]}>
                        {participantesSelecionados.includes(participante.id) && (
                          <Check size={12} color="#FFFFFF" />
                        )}
                      </View>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            )}

            <View style={styles.incluirCriadorSection}>
              <View style={styles.incluirCriadorRow}>
                <Text style={styles.incluirCriadorLabel}>Me incluir no grupo</Text>
                <Switch
                  value={incluirCriador}
                  onValueChange={(value) => {
                    toggleIncluirCriador(value);
                  }}
                  trackColor={{ false: colors.border, true: colors.primary + '40' }}
                  thumbColor={incluirCriador ? colors.primary : colors.textSecondary}
                />
              </View>
              <Text style={styles.incluirCriadorDescription}>
                Será adicionado automaticamente como participante do grupo
              </Text>
            </View>
              </>
            )}
          </ScrollView>
        </SafeAreaView>
      </Modal>

      {/* Modal Editar Grupo */}
      <Modal
        visible={showEditModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowEditModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity onPress={() => setShowEditModal(false)}>
              <Text style={styles.modalCancelButton}>Cancelar</Text>
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Editar Grupo</Text>
            <TouchableOpacity onPress={editarGrupo}>
              <Text style={styles.modalCreateButton}>Salvar</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.modalContent}>
            <View style={styles.emojiSelector}>
              <Text style={styles.selectedEmoji}>{novoGrupoEmoji}</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.emojiList}>
                  {['👥', '👨‍👩‍👧‍👦', '🏠', '🎉', '🍻', '✈️', '🏖️', '🎬', '🏋️', '🎮'].map((emoji) => (
                    <TouchableOpacity
                      key={emoji}
                      style={[
                        styles.emojiOption,
                        novoGrupoEmoji === emoji && styles.emojiSelected
                      ]}
                      onPress={() => setNovoGrupoEmoji(emoji)}
                    >
                      <Text style={styles.emojiOptionText}>{emoji}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.inputLabel}>Nome do grupo</Text>
              <TextInput
                style={styles.input}
                placeholder="Ex: Família, Amigos da faculdade"
                placeholderTextColor={colors.textSecondary}
                value={novoGrupoNome}
                onChangeText={setNovoGrupoNome}
                maxLength={30}
              />
            </View>

            {/* Lista de participantes existentes */}
            {editingGrupo && editingGrupo.participantes.length > 0 && (
              <View style={styles.participantesSection}>
                <Text style={styles.sectionTitle}>Participantes atuais</Text>
                <View style={styles.participantesList}>
                  {editingGrupo.participantes.map((participante: any) => (
                    <View key={participante.id} style={styles.participanteItem}>
                      <Image source={{ uri: participante.foto }} style={styles.participanteFoto} />
                      <View style={styles.participanteInfo}>
                        <Text style={styles.participanteNome}>{participante.nome}</Text>
                        {participante.email && (
                          <Text style={styles.participanteEmail}>{participante.email}</Text>
                        )}
                        {participante.chavePix && (
                          <Text style={styles.participantePix}>
                            PIX: {participante.chavePix.valor} ({participante.chavePix.tipo})
                          </Text>
                        )}
                      </View>
                      <TouchableOpacity
                        style={styles.removeButton}
                        onPress={() => confirmarRemocaoParticipante(editingGrupo.id, participante.id, participante.nome)}
                        activeOpacity={0.7}
                      >
                        <UserMinus size={16} color={colors.error} />
                      </TouchableOpacity>
                    </View>
                  ))}
                </View>
              </View>
            )}

            {/* Seção para adicionar novos participantes */}
            {todosParticipantes.length > 0 && (
              <View style={styles.participantesSection}>
                <Text style={styles.sectionTitle}>Adicionar participantes existentes</Text>
                <View style={styles.participantesDisponiveis}>
                  {todosParticipantes.map((participante) => (
                    <TouchableOpacity
                      key={participante.id}
                      style={[
                        styles.participanteOption,
                        participantesSelecionados.includes(participante.id) && styles.participanteSelected
                      ]}
                      onPress={() => toggleParticipante(participante.id)}
                    >
                      <Image source={{ uri: participante.foto }} style={styles.participanteFotoSmall} />
                      <Text style={styles.participanteOptionNome}>{participante.nome}</Text>
                      <View style={[
                        styles.checkbox,
                        participantesSelecionados.includes(participante.id) && styles.checkboxSelected
                      ]}>
                        {participantesSelecionados.includes(participante.id) && (
                          <Check size={12} color="#FFFFFF" />
                        )}
                      </View>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            )}

            {/* Opção para incluir o criador */}
            <View style={styles.incluirCriadorSection}>
              <View style={styles.incluirCriadorRow}>
                <Text style={styles.incluirCriadorLabel}>Me incluir no grupo</Text>
                <Switch
                  value={incluirCriador}
                  onValueChange={(value) => {
                    toggleIncluirCriador(value);
                  }}
                  trackColor={{ false: colors.border, true: colors.primary + '40' }}
                  thumbColor={incluirCriador ? colors.primary : colors.textSecondary}
                />
              </View>
              <Text style={styles.incluirCriadorDescription}>
                Será adicionado automaticamente como participante do grupo
              </Text>
            </View>
          </View>
        </SafeAreaView>
      </Modal>

      {/* Modal Adicionar Participante */}
      <Modal
        visible={showAddParticipanteModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowAddParticipanteModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity onPress={() => setShowAddParticipanteModal(false)}>
              <Text style={styles.modalCancelButton}>Cancelar</Text>
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Adicionar Participante</Text>
            <TouchableOpacity onPress={() => {
              adicionarParticipante();
            }}>
              <Text style={styles.modalCreateButton}>Adicionar</Text>
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.modalContent}>
            {/* Seção para adicionar novo participante */}
            <Text style={styles.sectionTitle}>Adicionar novo participante</Text>
            
            <View style={styles.inputContainer}>
              <Text style={styles.inputLabel}>Nome completo</Text>
              <TextInput
                style={styles.input}
                placeholder="Digite o nome do participante"
                placeholderTextColor={colors.textSecondary}
                value={novoParticipante.nome}
                onChangeText={(text) => setNovoParticipante(prev => ({ ...prev, nome: text }))}
              />
            </View>

            <View style={styles.inputContainer}>
              <View style={styles.labelRow}>
                <Text style={styles.inputLabel}>Email</Text>
                <Text style={styles.optionalLabel}>(campo opcional)</Text>
              </View>
              <TextInput
                style={styles.input}
                placeholder="Digite o email do participante"
                placeholderTextColor={colors.textSecondary}
                value={novoParticipante.email}
                onChangeText={(text) => setNovoParticipante(prev => ({ ...prev, email: text }))}
                keyboardType="email-address"
                autoCapitalize="none"
              />
            </View>

            <View style={styles.inputContainer}>
              <View style={styles.labelRow}>
                <Text style={styles.inputLabel}>Chave PIX</Text>
                <Text style={styles.optionalLabel}>(campo opcional)</Text>
              </View>
              <ChavePixSelector
                value={novoParticipante.chavePix}
                onChange={(chavePix) => setNovoParticipante(prev => ({ ...prev, chavePix }))}
                placeholder="Configure a chave PIX do participante"
              />
            </View>

            {/* Opção para incluir o criador */}
            <View style={styles.incluirCriadorSection}>
              <View style={styles.incluirCriadorRow}>
                <Text style={styles.incluirCriadorLabel}>Me incluir no grupo</Text>
                <Switch
                  value={incluirCriador}
                  onValueChange={(value) => {
                    toggleIncluirCriador(value);
                  }}
                  trackColor={{ false: colors.border, true: colors.primary + '40' }}
                  thumbColor={incluirCriador ? colors.primary : colors.textSecondary}
                />
              </View>
              <Text style={styles.incluirCriadorDescription}>
                Será adicionado automaticamente como participante do grupo
              </Text>
            </View>

            {/* Seção para adicionar participantes existentes */}
            {todosParticipantes.length > 0 && (
              <>
                <View style={styles.divider} />
                <Text style={styles.dividerText}>OU</Text>
                
                <Text style={styles.sectionTitle}>Adicionar participantes existentes</Text>
                <View style={styles.participantesDisponiveis}>
                  {todosParticipantes.map((participante) => (
                    <TouchableOpacity
                      key={participante.id}
                      style={[
                        styles.participanteOption,
                        participantesSelecionados.includes(participante.id) && styles.participanteSelected
                      ]}
                      onPress={() => toggleParticipante(participante.id)}
                    >
                      <Image source={{ uri: participante.foto }} style={styles.participanteFotoSmall} />
                      <Text style={styles.participanteOptionNome}>{participante.nome}</Text>
                      <View style={[
                        styles.checkbox,
                        participantesSelecionados.includes(participante.id) && styles.checkboxSelected
                      ]}>
                        {participantesSelecionados.includes(participante.id) && (
                          <Check size={12} color="#FFFFFF" />
                        )}
                      </View>
                    </TouchableOpacity>
                  ))}
                </View>
              </>
            )}
          </ScrollView>
        </SafeAreaView>
      </Modal>

      {/* Alerta Nativo */}
      {showNativeAlert && (
        <View style={styles.alertOverlay}>
          <View style={styles.alertContainer}>
            <Text style={styles.alertTitle}>{alertConfig.title}</Text>
            <Text style={styles.alertMessage}>{alertConfig.message}</Text>
            <View style={styles.alertButtons}>
              {alertConfig.showCancel && (
                <TouchableOpacity
                  style={[styles.alertButton, styles.alertButtonCancel]}
                  onPress={() => setShowNativeAlert(false)}
                >
                  <Text style={styles.alertButtonCancelText}>Cancelar</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity
                style={[styles.alertButton, styles.alertButtonConfirm]}
                onPress={() => {
                  setShowNativeAlert(false);
                  if (alertConfig.onConfirm) {
                    alertConfig.onConfirm();
                  }
                }}
              >
                <Text style={styles.alertButtonConfirmText}>
                  {alertConfig.showCancel ? 'Confirmar' : 'OK'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* Modal de Migração */}
      <MigrationModal
        visible={showMigrationModal}
        onClose={() => setShowMigrationModal(false)}
        onMigrate={startMigration}
        onSkip={() => setShowMigrationModal(false)}
      />
    </SafeAreaView>
  );
}
